import { useTranslation } from "@/hooks/useTranslation";
import { getAllTimezones } from "@/utils/timezones";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Checkbox,
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
	SmartSelect,
} from "@st-marketplace/ui";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import {
	type SenderGroupFormData,
	type SenderGroupInitialValues,
	getSenderGroupSchema,
} from "./senderGroupForm.schema";

type Props = {
	submitLabel: string;
	initialValues: SenderGroupInitialValues;
	loading?: boolean;
	onSubmit: (data: SenderGroupFormData) => Promise<void> | void;
	onReset?: () => void;
};

const ALL_TIMEZONES: readonly string[] = getAllTimezones();

export const SenderGroupForm: React.FC<Props> = ({
	submitLabel,
	initialValues,
	loading = false,
	onSubmit,
	onReset,
}) => {
	const { t } = useTranslation();
	const schema = React.useMemo(() => getSenderGroupSchema(t), [t]);

	const priorityItems = useMemo(
		() => [
			{ value: "EXCLUDED", label: t("enums.sender_priority.excluded") },
			{ value: "LOW", label: t("enums.sender_priority.low") },
			{ value: "MEDIUM", label: t("enums.sender_priority.medium") },
			{ value: "HIGH", label: t("enums.sender_priority.high") },
			{
				value: "VERY_HIGH",
				label: t("enums.sender_priority.very_high"),
			},
		],
		[t],
	);

	const timezoneItems = useMemo(
		() => ALL_TIMEZONES.map((tz) => ({ value: tz, label: tz })),
		[],
	);

	const form = useForm<SenderGroupFormData>({
		resolver: zodResolver(schema),
		defaultValues: {
			name: initialValues.name,
			timezone: initialValues.timezone ?? "",
			to_keep: initialValues.to_keep,
			use_global_setting: initialValues.use_global_setting,
			use_global_priority: initialValues.use_global_priority,
			priority_usage: initialValues.priority_usage,
		},
	});

	const handleReset = () => {
		form.reset();
		onReset?.();
	};

	const handleSubmit = form.handleSubmit(async (data) => {
		await onSubmit(data);
		handleReset();
	});

	const useGlobalSetting = form.watch("use_global_setting");
	const useGlobalPriority = form.watch("use_global_priority");

	return (
		<div>
			<Form {...form}>
				<form
					noValidate
					onSubmit={handleSubmit}
					className="grid grid-cols-12 gap-5"
				>
					<div className="col-span-12">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>{t("senderGroup.formName")}</FormLabel>
									<FormControl>
										<Input
											{...field}
											ltr
											placeholder={t("senderGroup.formName")}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="col-span-12">
						<FormField
							control={form.control}
							name="timezone"
							render={({ field }) => (
								<FormItem>
									<FormLabel required>
										{t("senderGroup.formTimezone")}
									</FormLabel>
									<FormControl>
										<SmartSelect
											items={timezoneItems}
											value={field.value ?? ""}
											onChange={field.onChange}
											searchable
											placeholder={t("timezone.placeholder")}
											searchPlaceholder={t("timezone.searchPrompt")}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="col-span-12">
						<Checkbox
							label={t("senderGroup.formUseGlobalSetting")}
							checked={useGlobalSetting}
							onCheckedChange={(checked) => {
								const next = checked === true;
								form.setValue("use_global_setting", next, {
									shouldDirty: true,
								});
								if (next) {
									form.setValue("to_keep", null, {
										shouldDirty: true,
									});
								}
							}}
						/>
						<div className="min-h-11.5">
							{useGlobalSetting ? (
								<FormDescription>
									{t("senderGroup.formGlobalSettingHelper")}
								</FormDescription>
							) : (
								<FormField
									control={form.control}
									name="to_keep"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												{t("senderGroup.formGroupDefaultKeep")}
											</FormLabel>
											<FormControl>
												<Input
													type="number"
													placeholder={t("senderGroup.formGroupDefaultKeep")}
													value={field.value ?? ""}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</div>

					<div className="col-span-12">
						<Checkbox
							label={t("senderGroup.formUseGlobalPriority")}
							checked={useGlobalPriority}
							onCheckedChange={(checked) => {
								const next = checked === true;
								form.setValue("use_global_priority", next, {
									shouldDirty: true,
								});
								if (next) {
									form.setValue("priority_usage", null, {
										shouldDirty: true,
									});
								}
							}}
						/>
						<div className="min-h-11.5">
							{useGlobalPriority ? (
								<FormDescription>
									{t("senderGroup.formGlobalPriorityHelper")}
								</FormDescription>
							) : (
								<FormField
									control={form.control}
									name="priority_usage"
									render={({ field }) => (
										<FormItem>
											<FormLabel required>
												{t("senderGroup.formGroupPriority")}
											</FormLabel>
											<FormControl>
												<SmartSelect
													items={priorityItems}
													value={field.value ?? undefined}
													onChange={field.onChange}
													placeholder={t("senderGroup.formGroupPriority")}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							)}
						</div>
					</div>

					<div className="col-span-12 md:col-span-6">
						<Button
							type="button"
							color="secondary"
							className="w-full"
							onClick={handleReset}
						>
							{t("proxy.new.formReset")}
						</Button>
					</div>

					<div className="col-span-12 md:col-span-6">
						<Button
							type="submit"
							color="primary"
							className="w-full"
							loading={loading}
						>
							{submitLabel}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
};
